import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Eye, 
  AlertTriangle, 
  TrendingUp, 
  Shield,
  Globe,
  Search,
  Filter,
  Download,
  Activity,
  ExternalLink,
  Lock,
  Unlock,
  Radio,
  Users,
  Database
} from "lucide-react";

export default function Trackers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [activeCardFilter, setActiveCardFilter] = useState<string | null>(null); // 'total' | 'active' | 'pixel' | 'privacy' | null

  const { data: trackersData, isLoading } = useQuery({
    queryKey: ["/api/trackers"],
    refetchInterval: 60000, // Refresh every minute
  });

  const trackers: any[] = trackersData?.trackers || [];
  const summary = trackersData?.summary || {
    totalTrackers: 0,
    activeTrackers: 0,
    pixelTrackers: 0,
    crossDomainTrackers: 0,
    privacyViolations: 0,
    riskDistribution: { high: 0, medium: 0, low: 0 },
    vendorBreakdown: {}
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "analytics": return "bg-blue-100 text-blue-800 border-blue-200";
      case "social": return "bg-purple-100 text-purple-800 border-purple-200";
      case "marketing": return "bg-orange-100 text-orange-800 border-orange-200";
      case "advertising": return "bg-red-100 text-red-800 border-red-200";
      case "heatmap": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "pixel": return "bg-pink-100 text-pink-800 border-pink-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "high": return "bg-red-100 text-red-800 border-red-200";
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getVendorIcon = (vendor: string) => {
    switch (vendor.toLowerCase()) {
      case "google": return "🔍";
      case "meta/facebook": return "📘";
      case "hubspot": return "🧡";
      case "hotjar": return "🔥";
      case "mixpanel": return "📊";
      case "segment": return "🔗";
      case "amplitude": return "📈";
      default: return "🌐";
    }
  };

  const handleCardClick = (filterType: 'total' | 'active' | 'pixel' | 'privacy') => {
    if (activeCardFilter === filterType) {
      setActiveCardFilter(null);
    } else {
      setActiveCardFilter(filterType);
    }
  };

  const filteredTrackers = trackers.filter((tracker: any) => {
    const matchesSearch = 
      tracker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tracker.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tracker.purpose.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || tracker.type === filterType;
    const matchesRisk = filterRisk === "all" || tracker.riskLevel === filterRisk;
    
    // Apply card filter
    let matchesCardFilter = true;
    if (activeCardFilter === 'active') {
      matchesCardFilter = tracker.isActive === true;
    } else if (activeCardFilter === 'pixel') {
      matchesCardFilter = tracker.type === 'pixel' || tracker.type === 'tracking-pixel';
    } else if (activeCardFilter === 'privacy') {
      matchesCardFilter = tracker.hasPrivacyViolation === true || tracker.riskLevel === 'high';
    }
    // 'total' or null means show all
    
    return matchesSearch && matchesType && matchesRisk && matchesCardFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Third-Party Trackers & Pixels
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Monitor tracking scripts and privacy compliance across your domains
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/trackers';
              link.download = `trackers-report-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Badge variant="secondary" className="text-xs">
            <Activity className="h-3 w-3 mr-1" />
            {summary.totalTrackers} Trackers
          </Badge>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'active' ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950' : ''
          }`}
          onClick={() => handleCardClick('active')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'active' ? 'bg-blue-200' : 'bg-blue-100'
              }`}>
                <Eye className={`h-5 w-5 ${
                  activeCardFilter === 'active' ? 'text-blue-700' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.activeTrackers}
                </div>
                <div className="text-xs text-slate-600">Active Trackers</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'active' ? 'text-blue-700 font-semibold' : 'text-blue-600'
                }`}>
                  <Globe className="w-3 h-3 inline mr-1" />
                  {summary.crossDomainTrackers} cross-domain
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'pixel' ? 'ring-2 ring-pink-500 bg-pink-50 dark:bg-pink-950' : ''
          }`}
          onClick={() => handleCardClick('pixel')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'pixel' ? 'bg-pink-200' : 'bg-pink-100'
              }`}>
                <Radio className={`h-5 w-5 ${
                  activeCardFilter === 'pixel' ? 'text-pink-700' : 'text-pink-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.pixelTrackers}
                </div>
                <div className="text-xs text-slate-600">Pixel Tracking</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'pixel' ? 'text-pink-700 font-semibold' : 'text-purple-600'
                }`}>
                  <Shield className="w-3 h-3 inline mr-1" />
                  Most compliant
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'privacy' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-950' : ''
          }`}
          onClick={() => handleCardClick('privacy')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'privacy' ? 'bg-red-200' : 'bg-red-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'privacy' ? 'text-red-700' : 'text-red-600'
                }`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${
                  activeCardFilter === 'privacy' ? 'text-red-700' : 'text-red-600'
                }`}>
                  {summary.privacyViolations}
                </div>
                <div className="text-xs text-slate-600">Privacy Issues</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'privacy' ? 'text-red-700 font-semibold' : 'text-slate-500'
                }`}>
                  Compliance violations
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'total' ? 'ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-950' : ''
          }`}
          onClick={() => handleCardClick('total')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'total' ? 'bg-emerald-200' : 'bg-emerald-100'
              }`}>
                <Users className={`h-5 w-5 ${
                  activeCardFilter === 'total' ? 'text-emerald-700' : 'text-purple-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {Object.keys(summary.vendorBreakdown || {}).length}
                </div>
                <div className="text-xs text-slate-600">Unique Vendors</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'total' ? 'text-emerald-700 font-semibold' : 'text-purple-600'
                }`}>
                  <Database className="w-3 h-3 inline mr-1" />
                  Tracked services
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Risk Distribution</CardTitle>
          <CardDescription>
            Breakdown of tracker risk levels based on privacy impact and data collection
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm">High Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{summary.riskDistribution.high}</span>
                <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-red-500 transition-all duration-300"
                    style={{ width: `${(summary.riskDistribution.high / Math.max(summary.totalTrackers, 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-sm">Medium Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{summary.riskDistribution.medium}</span>
                <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 transition-all duration-300"
                    style={{ width: `${(summary.riskDistribution.medium / Math.max(summary.totalTrackers, 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span className="text-sm">Low Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{summary.riskDistribution.low}</span>
                <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 transition-all duration-300"
                    style={{ width: `${(summary.riskDistribution.low / Math.max(summary.totalTrackers, 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search trackers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="analytics">Analytics</option>
            <option value="social">Social</option>
            <option value="marketing">Marketing</option>
            <option value="advertising">Advertising</option>
            <option value="pixel">Pixels</option>
          </select>
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Risk Levels</option>
            <option value="high">High Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="low">Low Risk</option>
          </select>
        </div>
      </div>

      {/* Trackers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Detected Trackers ({filteredTrackers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading trackers...</span>
            </div>
          ) : filteredTrackers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Eye className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No Trackers Found</div>
              <div className="text-sm">
                {searchTerm || filterType !== "all" || filterRisk !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "No tracking scripts detected in your recent scans."}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTrackers.map((tracker: any) => (
                <div
                  key={tracker.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-2xl">
                      {getVendorIcon(tracker.vendor)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{tracker.name}</span>
                        <Badge className={`text-xs border ${getRiskColor(tracker.riskLevel)}`}>
                          {tracker.riskLevel} risk
                        </Badge>
                        <Badge className={`text-xs border ${getTypeColor(tracker.type)}`}>
                          {tracker.type}
                        </Badge>
                        {tracker.crossDomain && (
                          <Badge variant="outline" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" />
                            Cross-domain
                          </Badge>
                        )}
                        {tracker.encrypted ? (
                          <Lock className="h-3 w-3 text-purple-600" />
                        ) : (
                          <Unlock className="h-3 w-3 text-red-600" />
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mb-1">
                        {tracker.purpose}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>Vendor: {tracker.vendor}</span>
                        <span>Domain: {tracker.domain}</span>
                        {tracker.complianceIssues.length > 0 && (
                          <span className="text-red-600">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />
                            {tracker.complianceIssues.length} compliance issue{tracker.complianceIssues.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}